import { apiClient } from './client';

function buildQueryParams(params = {}) {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value);
    }
  });
  const query = searchParams.toString();
  return query ? `?${query}` : '';
}

const STORAGE_KEY = "barcode_label_records_v1";

const INITIAL_MOCK_DATA = [];

function getStoredLabels() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

function saveStoredLabels(labels) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(labels));
  } catch (err) {
    console.error("Error saving barcode labels to localStorage:", err);
  }
}

export const barcodeLabelService = {
  list: async (params = {}) => {
    try {
      const query = buildQueryParams(params);
      const res = await apiClient.get(`/master/barcode-labels${query}`);
      if (res && res.data) {
        return res;
      }
    } catch (err) {
      console.warn("Backend barcode label list fetch failed, fallback to local:", err?.message);
    }

    // Fallback to local storage
    let labels = getStoredLabels();
    const { search = "", status = "", page = 1, limit = 10 } = params;

    if (search) {
      const q = search.toLowerCase();
      labels = labels.filter(
        (l) => l.name.toLowerCase().includes(q) || l.size.toLowerCase().includes(q)
      );
    }

    if (status) {
      labels = labels.filter((l) => l.status === status);
    }

    const total = labels.length;
    const startIndex = (page - 1) * limit;
    const paginated = labels.slice(startIndex, startIndex + limit);

    return {
      data: paginated,
      meta: {
        page: Number(page),
        limit: Number(limit),
        total,
      },
    };
  },

  listAll: async () => {
    const allLabels = [];
    let page = 1;
    let total = 0;
    do {
      const response = await barcodeLabelService.list({ page, limit: 100 });
      const rows = response?.data || [];
      allLabels.push(...rows);
      total = Number(response?.meta?.total || allLabels.length);
      if (rows.length === 0 || allLabels.length >= total) break;
      page += 1;
    } while (page <= 100);
    return allLabels;
  },

  get: async (id) => {
    try {
      const res = await apiClient.get(`/master/barcode-labels/${id}`);
      if (res && res.data) return res.data;
    } catch (err) {
      console.warn("Backend barcode label get failed, fallback to local:", err?.message);
    }

    const labels = getStoredLabels();
    return labels.find((l) => l.id === id) || null;
  },

  create: async (payload) => {
    try {
      const res = await apiClient.post(`/master/barcode-labels`, payload);
      if (res && res.data) return res.data;
    } catch (err) {
      if (/nama label sudah digunakan/i.test(err?.message || "")) {
        throw err;
      }
      console.warn("Backend barcode label create failed, fallback to local:", err?.message);
    }

    const labels = getStoredLabels();
    const newRecord = {
      id: "lbl-" + Date.now(),
      name: payload.name || "Label Barcode Tanpa Nama",
      size: payload.size || "40 × 30 mm",
      product_count: payload.product_count || (payload.items ? payload.items.length : 0),
      total_labels: payload.total_labels || (payload.items ? payload.items.reduce((s, i) => s + (i.qty || 1), 0) : 0),
      status: payload.status || "Disusun",
      created_at: new Date().toISOString(),
      display_fields: payload.display_fields || {
        show_product_name: true,
        show_price: true,
        show_sku: false,
        show_barcode: true,
      },
      print_settings: payload.print_settings || {},
      items: payload.items || [],
    };

    labels.unshift(newRecord);
    saveStoredLabels(labels);
    return newRecord;
  },

  updateStatus: async (id, status) => {
    try {
      const res = await apiClient.patch(`/master/barcode-labels/${id}/status`, { status });
      if (res && res.data) return res.data;
    } catch (err) {
      console.warn("Backend barcode label updateStatus failed, fallback to local:", err?.message);
    }

    const labels = getStoredLabels();
    const index = labels.findIndex((l) => l.id === id);
    if (index !== -1) {
      labels[index].status = status;
      saveStoredLabels(labels);
      return labels[index];
    }
    return null;
  },

  delete: async (id) => {
    try {
      await apiClient.delete(`/master/barcode-labels/${id}`);
      return true;
    } catch (err) {
      console.warn("Backend barcode label delete failed, fallback to local:", err?.message);
    }

    let labels = getStoredLabels();
    labels = labels.filter((l) => l.id !== id);
    saveStoredLabels(labels);
    return true;
  },
};

export default barcodeLabelService;
